import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';

import { toast } from '@savgroup-front-common/core/atoms/Toast';
import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';
import { useToasts } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import { BaseBackResponse } from '@savgroup-front-common/types';

import { PaymentService } from '@/api';
import { Kind } from '@/api/Payment/getPaymentSessionQuery';

import messages from '../messages';

export const GetPaymentSessionQuery = 'GetPaymentSessionQuery';

export const useGetPaymentSession = ({
  kindId,
  kind,
  sessionId,
  onConfirmPaymentIntent,
  onCreatePaymentIntent,
}: {
  kindId: string;
  kind: Kind;
  sessionId?: string;
  onConfirmPaymentIntent: (
    payload: Record<never, never>,
  ) => Promise<BaseBackResponse>;
  onCreatePaymentIntent: () => void;
}) => {
  const { pushErrors } = useToasts();
  const { search } = useLocation();
  const paylineToken = new URLSearchParams(search).get(
    'paylinetoken',
  ) as string;

  const { data: paymentSessionSummary, isLoading } = useQuery(
    [GetPaymentSessionQuery, { kindId, sessionId }],
    async () => {
      if (!sessionId || !paylineToken) {
        return;
      }

      const response = await PaymentService.getPaymentSessionQuery({
        kindId,
        kind,
        sessionId,
      });

      if (response.failure) {
        if (response.errors[0].code === 'Reverse.Common.Errors.Data.NotFound') {
          return;
        }

        pushErrors(response.errors);

        return;
      }

      if (response.value.isPaymentInProgress) {
        return;
      }

      if (response.value.isPaymentValid) {
        toast.success(safeFormattedIntlString(messages.paymentSuccess));

        await onConfirmPaymentIntent({});
      } else {
        toast.error(safeFormattedIntlString(messages.paymentFailure));

        onCreatePaymentIntent();

        return undefined;
      }

      return response.value;
    },
    {
      staleTime: 1_000,
    },
  );

  return { paymentSessionSummary, isLoading };
};
